import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Eye } from 'lucide-react'
import { Button, credentialsText, unitsText } from '../../core/index.js'
import type { AdminClient } from '../../core/index.js'
import type { RosterAdminApi } from '../adminApi.js'
import { EMPLOYMENT_TYPE_LABELS } from '../types.js'
import type { AccountsAdminApi } from '../../accounts/index.js'
import type { BehaviorsAdminApi } from '../../behaviors/index.js'
import { unitNamesFromConfig, type FacilitiesAdminApi } from '../../facilities/index.js'
import { AdminWorkerReachSection, type MessagesAdminApi } from '../../messages/index.js'
import type { PartnerAdminApi } from '../../partner/index.js'
import { ShiftPreferencesSummary, describePeriod, type ScheduleAdminApi } from '../../schedule/index.js'
import type { ScoringAdminApi } from '../../scoring/index.js'
import { WorkerScoringPanel } from '../../scoring/index.js'
import { createAdminWorkerDetailHooks } from './adminWorkerDetailHooks.js'
import { formatScore } from '../../core/index.js'
import { writeHandoff } from '../../scoring/index.js'
import { navigate } from '../../core/index.js'
import { TIER_LABELS } from '../../core/index.js'
import { WORKER_ROLE_LABELS } from '../../core/index.js'
import { DetailFields, DetailSection, QueryBlock, formatDateTime } from '../../core/index.js'
import { WorkerLoginSection } from '../../accounts/index.js'
import { useFeatures } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = RosterAdminApi & FacilitiesAdminApi & AccountsAdminApi & ScoringAdminApi & ScheduleAdminApi & MessagesAdminApi & PartnerAdminApi & BehaviorsAdminApi & { readonly client: AdminClient }

interface AdminWorkerDetailProps {
  api: AdminApi
  clientId: string
  workerId: string
}

/**
 * What the platform admin sees for one worker: the same scoring panel an
 * operator opens on their roster - the dial, the breakdown with its tabs,
 * the current month and the history of every month - over the read-only
 * admin endpoints, so a question from a client is looked at on the same
 * screen they are looking at. Underneath, what only the admin needs: the
 * record's identifiers, the engine run behind the score with a way into
 * the Score Simulator, Clinical Bridge enrollment and claimed shifts. A
 * body with no frame of its own, so it can sit under a table row or
 * inside a dialog.
 */
export function AdminWorkerDetail({ api, clientId, workerId }: AdminWorkerDetailProps) {
  const hooks = useMemo(() => createAdminWorkerDetailHooks(api, clientId), [api, clientId])
  const features = useFeatures()
  const key = (part: string) => ['admin', 'worker', clientId, workerId, part]
  const base = `/admin/clients/…/workers/${workerId}`

  // The same keys the scoring panel reads under, so these are the panel's queries, not a second fetch.
  const profileQuery = hooks.useProfile(workerId)
  const scoreQuery = hooks.useScore(workerId)
  // Only for the facility's name, which the profile does not carry; the same query the facility lists use, so it is usually cached.
  const facilitiesQuery = useQuery({ queryKey: ['admin', 'facilities', clientId], queryFn: () => api.listFacilities(clientId) })
  // The facility's units by name, so the record says "Unit A" rather than its id.
  const workerFacilityId = profileQuery.data?.facilityId ?? null
  const facilityConfigQuery = useQuery({ queryKey: ['admin', 'facility-detail', clientId, workerFacilityId, 'config'], queryFn: () => api.facilityConfig(clientId, workerFacilityId as string), enabled: workerFacilityId !== null })
  const bridgeQuery = useQuery({ queryKey: key('clinical-bridge'), queryFn: () => api.workerClinicalBridge(clientId, workerId) })
  const preferencesQuery = useQuery({ queryKey: ['admin', 'client', clientId, 'worker', workerId, 'shift-preferences'], queryFn: () => api.workerShiftPreferences(clientId, workerId) })
  const draftQuery = useQuery({ queryKey: ['admin', 'client', clientId, 'worker', workerId, 'draft-schedule'], queryFn: () => api.workerDraftSchedule(clientId, workerId) })
  const shiftsQuery = useQuery({ queryKey: key('shifts'), queryFn: () => api.workerClaimedShifts(clientId, workerId) })

  return (
    <div>
      {/* The panel brings its own top border and canvas, the same as under an operator's row. */}
      <WorkerScoringPanel workerId={workerId} hooks={hooks} />

      <div className="border-t border-marine/15 bg-canvas px-5 pb-5 pt-4">
        <div className="mb-3 text-[9px] font-bold uppercase tracking-wide text-ink-mid">Platform admin</div>
        <div className="grid gap-6 md:grid-cols-2">
          <DetailSection title="Record" endpoint={`GET ${base}`}>
            <QueryBlock query={profileQuery} missing="This worker no longer exists.">
              {(profile) => (
                <DetailFields
                  fields={[
                    { label: 'Facility', value: facilitiesQuery.data?.find((facility) => facility.facilityId === profile.facilityId)?.name ?? profile.facilityId },
                    { label: 'Role', value: `${profile.role} · ${WORKER_ROLE_LABELS[profile.role]}` },
                    { label: 'Employment', value: EMPLOYMENT_TYPE_LABELS[profile.employmentType ?? 'full_time'] },
                    { label: 'Contracted hours', value: profile.contractedHoursPerWeek == null ? 'Not recorded' : `${profile.contractedHoursPerWeek} a week` },
                    { label: 'Units', value: unitsText(profile.units, unitNamesFromConfig(facilityConfigQuery.data)) },
                    { label: 'Credentials', value: credentialsText(profile.credentials) },
                    { label: 'Tier held', value: TIER_LABELS[profile.tier] },
                    { label: 'Score resets', value: formatDateTime(profile.scoreResetsAt) },
                    { label: 'Bid window opens', value: formatDateTime(profile.bidWindowOpensAt) },
                  ]}
                />
              )}
            </QueryBlock>
          </DetailSection>

          <DetailSection title="Engine run" endpoint="…/score">
            <QueryBlock query={scoreQuery} missing="No score yet.">
              {(score) => (
                <div className="flex flex-col gap-3">
                  <DetailFields
                    fields={[
                      { label: 'Period', value: `${score.from} to ${score.to}${score.provisional ? ' · provisional' : ''}` },
                      { label: 'Shifts', value: `${score.scheduledShifts} scheduled, ${score.workedShifts} worked` },
                      { label: 'Computed', value: formatDateTime(score.computedAt) },
                      { label: 'Engine', value: `v${score.engineVersion}${score.behaviorsVersion === null ? '' : ` · behaviors v${score.behaviorsVersion}`}`, mono: true },
                    ]}
                  />
                  <Button
                    icon={Eye}
                    variant="secondary"
                    size="sm"
                    className="self-start"
                    onClick={() => {
                      const workerName = profileQuery.data?.name ?? workerId
                      const facilityId = profileQuery.data?.facilityId ?? null
                      writeHandoff({
                        label: `${workerName}’s score of ${score.score === null ? 'nothing' : formatScore(score.score)} for ${score.from.slice(0, 7)}`,
                        settings: score.simulation,
                        subject: {
                          clientId,
                          workerId,
                          workerName,
                          facilityId,
                          facilityName: facilitiesQuery.data?.find((facility) => facility.facilityId === facilityId)?.name ?? null,
                          period: `${score.from} to ${score.to}`,
                        },
                      })
                      navigate('/admin/score-simulator', { rememberOrigin: true })
                    }}
                  >
                    Open in Score Simulator
                  </Button>
                </div>
              )}
            </QueryBlock>
          </DetailSection>

          <DetailSection title="Clinical Bridge" endpoint="…/clinical-bridge">
            <QueryBlock query={bridgeQuery} missing="Not enrolled.">
              {(bridge) => (
                <DetailFields
                  fields={[
                    { label: 'Eligible', value: bridge.eligible ? 'Yes' : 'No' },
                    { label: 'Facility', value: bridge.rules.enabled ? `On · ${bridge.rules.responseWindowMinutes} min response · ${bridge.rules.arrivalWindowMinutes} min arrival · +${bridge.rules.scoreBoostPerShift} pts/shift` : 'Off' },
                    { label: 'Enrolled days', value: bridge.preBidDates.length === 0 ? 'None' : bridge.preBidDates.map((entry) => `${entry.date}${entry.enrolledBy === 'import' ? ' (imported)' : ''}`).join(', ') },
                  ]}
                />
              )}
            </QueryBlock>
          </DetailSection>

          <DetailSection title="Shift preferences" endpoint="…/shift-preferences">
            <QueryBlock query={preferencesQuery} missing="Not available.">
              {(view) => (
                <div>
                  <div className="mb-1 text-[10px] font-bold text-ink-mid">{describePeriod(view.period)}</div>
                  <ShiftPreferencesSummary preferences={view.preferences} period={view.period} draft={draftQuery.data?.draft ?? null} isStale={draftQuery.data?.isStale ?? false} />
                </div>
              )}
            </QueryBlock>
          </DetailSection>

          <AdminWorkerReachSection api={api} clientId={clientId} workerId={workerId} />

          {/* The worker's own sign-in, through the facility-scoped admin path; the facility comes from the profile. */}
          <div className="md:col-span-2">
            <QueryBlock query={profileQuery} missing="This worker no longer exists.">
              {(profile) => (
                <WorkerLoginSection
                  api={api.workerLogin(clientId, profile.facilityId, workerId)}
                  queryKey={['admin', 'worker-login', clientId, workerId]}
                />
              )}
            </QueryBlock>
          </div>

          {/* Each add-on's read-only section (src/features/index.ts). */}
          {features.map((feature) =>
            feature.adminWorkerSection ? (
              <div key={feature.key} className="md:col-span-2">
                <feature.adminWorkerSection client={api.client} clientId={clientId} workerId={workerId} />
              </div>
            ) : null,
          )}

          <DetailSection title="Claimed shifts" endpoint="…/shifts">
            <QueryBlock query={shiftsQuery} missing="No shifts.">
              {(shifts) =>
                shifts.length === 0 ? (
                  <div className="text-xs text-ink-mid">No shifts claimed.</div>
                ) : (
                  <ul className="divide-y divide-marine/10 border border-marine/15 bg-white text-xs">
                    {shifts.map((shift) => (
                      <li key={shift.id} className="flex justify-between gap-3 px-3 py-1.5">
                        <span className="font-bold text-ink">
                          {shift.date} <span className="font-normal capitalize text-ink-mid">· {shift.period}</span>
                        </span>
                        <span className="text-ink-mid">
                          {shift.startTime}–{shift.endTime} · unit {shift.unitId}
                        </span>
                      </li>
                    ))}
                  </ul>
                )
              }
            </QueryBlock>
          </DetailSection>
        </div>
      </div>
    </div>
  )
}
